import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Home,
  User,
  Plus,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalendarioUnificadoPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewDays, setViewDays] = useState(14); // Show 14 days by default

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [propsRes, reservRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/properties`),
        axios.get(`${BACKEND_URL}/api/reservations/all`)
      ]);
      setProperties(propsRes.data);
      
      setReservations(reservRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      // If reservations/all doesn't exist, try to get reservations for each property
      try {
        const propsRes = await axios.get(`${BACKEND_URL}/api/properties`);
        setProperties(propsRes.data);
        
        
        // Fetch reservations for each property
        const allReservations = [];
        for (const prop of propsRes.data) {
          try {
            const res = await axios.get(`${BACKEND_URL}/api/reservations?property_id=${prop.id}`);
            allReservations.push(...res.data);
          } catch (e) {
            // Ignore individual errors
          }
        }
        setReservations(allReservations);
      } catch (e) {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate array of dates for the calendar view
  const getDaysArray = () => {
    const days = [];
    const startDate = new Date(currentDate);
    for (let i = 0; i < viewDays; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const days = getDaysArray();

  // Navigate to previous/next period
  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - viewDays);
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + viewDays);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Check if a date falls within a reservation
  const getReservationForDate = (propertyId, date) => {
    const dateStr = date.toISOString().split('T')[0];
    return reservations.find(res => {
      if (res.property_id !== propertyId) return false;
      const checkin = res.fecha_checkin?.split('T')[0];
      const checkout = res.fecha_checkout?.split('T')[0];
      return dateStr >= checkin && dateStr < checkout;
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmada':
      case 'checked_in':
        return 'bg-blue-500';
      case 'pendiente':
        return 'bg-yellow-500';
      case 'checked_out':
        return 'bg-gray-400';
      default:
        return 'bg-blue-500';
    }
  };

  // Get OTA color
  const getOTAColor = (fuente) => {
    switch (fuente?.toLowerCase()) {
      case 'booking.com':
      case 'booking':
        return 'bg-blue-600';
      case 'airbnb':
        return 'bg-red-500';
      case 'expedia':
        return 'bg-yellow-600';
      case 'directo':
      case 'web':
        return 'bg-green-600';
      default:
        return 'bg-gray-500';
    }
  };

  // Format date for header
  const formatDate = (date) => {
    return date.toLocaleDateString('es-ES', { 
      weekday: 'short', 
      day: 'numeric',
      month: 'short'
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Calculate occupancy stats
  const calculateOccupancy = () => {
    const today = new Date().toISOString().split('T')[0];
    let occupied = 0;
    properties.forEach(prop => {
      const hasReservation = reservations.some(res => {
        const checkin = res.fecha_checkin?.split('T')[0];
        const checkout = res.fecha_checkout?.split('T')[0];
        return res.property_id === prop.id && today >= checkin && today < checkout;
      });
      if (hasReservation) occupied++;
    });
    return {
      occupied,
      total: properties.length,
      percentage: properties.length > 0 ? Math.round((occupied / properties.length) * 100) : 0
    };
  };

  const occupancy = calculateOccupancy();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          {/* Row 1: Property info (always visible, no scroll) */}
          <div className="flex items-center gap-3">
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[200px] sm:w-[260px] h-9 text-sm font-medium" data-testid="calendario-property-selector">
                  <Home className="h-4 w-4 mr-1 shrink-0" />
                  <SelectValue placeholder="Propiedad..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {(() => {
              const selProp = properties.find(p => p.id === selectedProperty);
              return selProp?.codigo_unico ? (
                <Badge className="bg-primary/10 text-primary border border-primary/20 text-xs font-mono px-3 py-1 shrink-0" data-testid="calendario-prop-code">{selProp.codigo_unico}</Badge>
              ) : null;
            })()}
          </div>
          {/* Row 2: Title + navigation controls */}
          <div className="flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
              Calendario
            </h1>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={goToToday}>
                Hoy
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={navigatePrev}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={navigateNext}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Select value={viewDays.toString()} onValueChange={(v) => setViewDays(parseInt(v))}>
                <SelectTrigger className="w-20 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="14">14 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm">Ocupación Hoy</p>
                  <p className="text-3xl font-bold">{occupancy.percentage}%</p>
                </div>
                <CalendarIcon className="h-10 w-10 text-slate-400" />
              </div>
              <p className="text-slate-300 text-sm mt-2">
                {occupancy.occupied} de {occupancy.total} ocupados
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Total Apartamentos</p>
                  <p className="text-3xl font-bold">{properties.length}</p>
                </div>
                <Home className="h-10 w-10 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Reservas Activas</p>
                  <p className="text-3xl font-bold">
                    {reservations.filter(r => ['confirmada', 'checked_in'].includes(r.estado)).length}
                  </p>
                </div>
                <User className="h-10 w-10 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <Button className="w-full bg-slate-600 hover:bg-slate-500">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Reserva
              </Button>
              <Button variant="outline" className="w-full mt-2">
                <Home className="h-4 w-4 mr-2" />
                Agregar Apartamento
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-600 rounded"></div>
            <span>Booking.com</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span>Airbnb</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-600 rounded"></div>
            <span>Expedia</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-600 rounded"></div>
            <span>Directo/Web</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-200 rounded"></div>
            <span>Disponible</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Header Row with Dates */}
              <div className="flex border-b">
                <div className="w-48 flex-shrink-0 p-3 bg-muted font-medium border-r">
                  Apartamento
                </div>
                {days.map((day, idx) => (
                  <div 
                    key={idx} 
                    className={`flex-1 min-w-[60px] p-2 text-center text-xs border-r ${
                      isToday(day) ? 'bg-emerald-50 font-bold' : 'bg-muted'
                    }`}
                  >
                    <div className={isToday(day) ? 'text-sky-600' : ''}>
                      {formatDate(day)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Property Rows */}
              {properties.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Home className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tienes apartamentos registrados</p>
                  <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar tu primer apartamento
                  </Button>
                </div>
              ) : (
                properties.map((property) => (
                  <div key={property.id} className="flex border-b hover:bg-muted/30">
                    {/* Property Name */}
                    <div className="w-48 flex-shrink-0 p-3 border-r flex items-center gap-2">
                      <Home className="h-4 w-4 text-slate-600" />
                      <div className="truncate">
                        <p className="font-medium truncate">{property.nombre}</p>
                        <p className="text-xs text-muted-foreground">{property.ciudad}</p>
                      </div>
                    </div>
                    
                    {/* Calendar Cells */}
                    {days.map((day, idx) => {
                      const reservation = getReservationForDate(property.id, day);
                      const isFirstDay = reservation && 
                        day.toISOString().split('T')[0] === reservation.fecha_checkin?.split('T')[0];
                      
                      return (
                        <div 
                          key={idx} 
                          className={`flex-1 min-w-[60px] p-1 border-r relative ${
                            isToday(day) ? 'bg-sky-50/50' : ''
                          }`}
                        >
                          {reservation ? (
                            <div 
                              className={`h-8 ${getOTAColor(reservation.fuente)} rounded-sm flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}
                              title={`${reservation.guest_name} ${reservation.guest_lastname || ''} - ${reservation.fuente}`}
                            >
                              {isFirstDay && (
                                <span className="text-[10px] text-white truncate px-1">
                                  {reservation.guest_name}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="h-8 bg-gray-100 rounded-sm hover:bg-gray-200 cursor-pointer transition-colors"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CalendarIcon className="h-5 w-5 text-sky-600 mt-0.5" />
              <div>
                <p className="font-medium text-sky-800">Vista Unificada</p>
                <p className="text-sm text-sky-700">
                  Este calendario te permite ver la ocupación de todos tus apartamentos en una sola vista. 
                  Cada apartamento mantiene su configuración independiente de tarifas y conexiones OTA.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
